import React, { useState, useEffect } from 'react';
import {
    TextField,
    Box,
    Tabs,
    Tab,
    IconButton,
    Button,
    Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { inputStyle } from '../styles';
import {BASIC_URL} from '../enum';
import TranslatableField from "./TranslatableField";
import CoordinatePickerMap from "./CoordinatePickerMap";
import {isInSaarlandBounds} from "../utils/GeoUtils";
import {geoCoding} from "../service/geocoding/GeoCoding";
import TranslatableAutocomplete from "./TranslatableAutocomplete";

const getOppositeLang = (lang) => (lang === 'de' ? 'en' : 'de');

const CompanyEntry = ({ localedIndustrieObject, onUpdate, onDelete, userCompany, companyType, companySize }) => {
    const { t } = useTranslation();

    const [formData, setFormData] = useState({ shared: {}, de: {}, en: {} });
    const [activeLang, setActiveLang] = useState('de');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [success, setSuccess] = useState('');
    const [availableTechnologies, setAvailableTechnologies] = useState([]);
    const [availableTypeOfIndustries, setAvailableTypeOfIndustries] = useState([]);
    const [availableProductServices, setAvailableProductServices] = useState([]);
    const [availableFieldProfiles, setAvailableFieldProfiles] = useState([]);
    const [showSaarlandBorderAlert, setShowSaarlandBroderAlert] = useState(false);

    const handleChange = (section, name, value) => {
        setFormData((prev) => ({
            ...prev,
            [section]: {...prev[section], [name]: value},
        }));
    }

    const handleAddressChange = async (name, value) => {
        const updatedShared = {
            ...formData.shared,
            [name]: value,
        };

        setFormData((prev) => ({
            ...prev,
            shared: updatedShared,
        }));

        const { street, zip, location } = updatedShared;

        if (street && zip && location) {
            const coords = await geoCoding(street, zip, location);
            if (coords && isInSaarlandBounds(coords.lat, coords.lng)) {
                setFormData((prev) => ({
                    ...prev,
                    shared: {
                        ...prev.shared,
                        latitude: coords.lat,
                        longitude: coords.lng,
                    },
                }));
              setShowSaarlandBroderAlert(false)
            } else {
                console.warn('Adresse liegt außerhalb des Saarlands. Koordinaten werden ignoriert.');
                setShowSaarlandBroderAlert(true)
                setFormData((prev) => ({
                  ...prev,
                  shared: {...prev.shared,
                    latitude:'',
                    longitude: ''
                  },
              }));
            }
        }
    };


    useEffect(() => {
        if (!localedIndustrieObject) return;
        const sourceLocale = localedIndustrieObject.de || localedIndustrieObject.en || {};
        setFormData({
            shared: {
                street: sourceLocale.Street_and_haus_number || '',
                zip: sourceLocale.ZIP_Code || '',
                location: sourceLocale.Location || '',
                email: sourceLocale.Email || '',
                website: sourceLocale.Website || '',
                websiteCareer: sourceLocale.Website_career || '',
                latitude: sourceLocale.Coordinates?.Latitude || '',
                longitude: sourceLocale.Coordinates?.Longitude || '',
            },
            de: localedIndustrieObject.de ? {
                industrieObject: localedIndustrieObject.de.Industrie_object_name || '',
                contact: localedIndustrieObject.de.Contact_information || '',
                technologies: localedIndustrieObject.de.technologies?.map((t) => ({
                    title: t.Name,
                    id: t.id,
                    documentId: t.documentId,
                })) || [],
                typeOfIndustries: localedIndustrieObject.de.type_of_industries?.map((t) => ({
                    title: t.Name,
                    id: t.id,
                    documentId: t.documentId,
                })) || [],
                productsAndServices: localedIndustrieObject.de.product_services?.map((t) => ({
                    title: t.Name,
                    id: t.id,
                    documentId: t.documentId,
                })) || [],
                fieldProfiles: localedIndustrieObject.de.field_profiles?.map((t) => ({
                    title: t.Name,
                    id: t.id,
                    documentId: t.documentId,
                })) || [],
            } : {},
            en: localedIndustrieObject.en ? {
                industrieObject: localedIndustrieObject.en.Industrie_object_name || '',
                contact: localedIndustrieObject.en.Contact_information || '',
                technologies: localedIndustrieObject.en.technologies?.map((t) => ({
                    title: t.Name,
                    id: t.id,
                    documentId: t.documentId,
                })) || [],
                typeOfIndustries: localedIndustrieObject.en.type_of_industries?.map((t) => ({
                    title: t.Name,
                    id: t.id,
                    documentId: t.documentId,
                })) || [],
                productsAndServices: localedIndustrieObject.en.product_services?.map((t) => ({
                    title: t.Name,
                    id: t.id,
                    documentId: t.documentId,
                })) || [],
                fieldProfiles: localedIndustrieObject.en.field_profiles?.map((t) => ({
                    title: t.Name,
                    id: t.id,
                    documentId: t.documentId,
                })) || [],
            } : {},
        });
    }, [localedIndustrieObject]);

    useEffect(() => {
        if (success || error) {
            const timer = setTimeout(() => {
                setSuccess('');
                setError('');
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [success, error]);

    useEffect(() => {
        const fetchTags = async (urlTagName, setMethod) => {
            try {
                const res = await axios.get(`${BASIC_URL}/api/${urlTagName}?pagination[pageSize]=1000&locale=${activeLang}`);
                const data = res.data.data.map((item) => ({
                    title: item.Name,
                    id: item.id,
                    documentId: item.documentId,
                }));
                setMethod(data);
            } catch (err) {
                console.error(`Failed to fetch ${urlTagName}`, err);
            }
        };
        fetchTags('technologies', setAvailableTechnologies);
        fetchTags('type-of-industries', setAvailableTypeOfIndustries);
        fetchTags('products-services', setAvailableProductServices);
        fetchTags('field-profiles', setAvailableFieldProfiles);
    }, [activeLang]);

    const handleDelete = async () => {
        for (const lang of ['de', 'en']) {
            try {
              if (!localedIndustrieObject.documentId.startsWith('temp')) {
                const token = localStorage.getItem('jwt');
                await axios.delete(
                    `${BASIC_URL}/api/industrie-objects/${localedIndustrieObject.documentId}?locale=${lang}`,
                    {headers: {Authorization: `Bearer ${token}`}}
                );
              }
                onDelete();
            } catch (err) {
                console.error('Delete failed:', err);
            }
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setError('');
        setSuccess('');

        const requiredFields = [
            { section: 'shared', name: 'street' },
            { section: 'shared', name: 'zip' },
            { section: 'shared', name: 'location' },
            { section: 'shared', name: 'email' },
            { section: 'shared', name: 'latitude' },
            { section: 'shared', name: 'longitude' },
        ];

        const missingFields = requiredFields.filter(({ section, name }) => {
            const val = formData[section]?.[name];
            return val === undefined || val === null || val === '';
        });

        if (missingFields.length > 0) {
            const newErrors = {};
            missingFields.forEach(({ section, name }) => {
                newErrors[`${section}.${name}`] = true;
            });

            setFieldErrors(newErrors);
            setError(t('company_form.validation_error'));
            setSaving(false);
            return;
        }

        const token = localStorage.getItem('jwt');

        const saveTags = async (input, lang, urlTagName) => {
            const existing = [];
            const newOnes = [];

            for (const item of input) {
                if (typeof item === 'string') newOnes.push(item);
                else existing.push(item.documentId);
            }

            const created = await Promise.all(
                newOnes.map(async (name) => {
                    const response = await axios.post(
                        `${BASIC_URL}/api/${urlTagName}?status=draft&locale=${lang}`,
                        { data: { Name: name } },
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    return response.data.data.documentId;
                })
            );

            return [...existing, ...created];
        }

        setFieldErrors({});

        try {
            let createdDocumentId = null;

            const langs = ['de', 'en'];
            for (let i = 0; i < langs.length; i++) {
                const lang = langs[i];
                const technologyTags = await saveTags(formData[lang].technologies || [], lang, 'technologies')
                const typeOfIndustriesTags = await saveTags(formData[lang].typeOfIndustries || [], lang, 'type-of-industries')
                const productsServicesTags = await saveTags(formData[lang].productsAndServices || [], lang, 'products-services')
                const fieldProfilesTags = await saveTags(formData[lang].fieldProfiles || [], lang, 'field-profiles')

                const payload = {
                    data: {
                        Company: userCompany,
                        Industrie_object_name: formData[lang].industrieObject,
                        Street_and_haus_number: formData.shared.street,
                        ZIP_Code: parseInt(formData.shared.zip) || null,
                        Location: formData.shared.location,
                        Email: formData.shared.email,
                        Website: formData.shared.website,
                        Website_career: formData.shared.websiteCareer,
                        Contact_information: formData[lang].contact,
                        company_type: companyType,
                        company_size: companySize,
                        locale: lang,
                        Coordinates: {
                            Latitude: formData.shared.latitude,
                            Longitude: formData.shared.longitude,
                        },
                        technologies: technologyTags,
                        type_of_industries: typeOfIndustriesTags,
                        product_services: productsServicesTags,
                        field_profiles: fieldProfilesTags
                    },
                };

                const isSaved = localedIndustrieObject.saved !== false;

                if (isSaved || createdDocumentId) {
                    const idToUse = createdDocumentId ||  localedIndustrieObject.documentId;
                    await axios.put(
                        `${BASIC_URL}/api/industrie-objects/${idToUse}?status=draft&locale=${lang}`,
                        payload,
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                } else {
                    const res = await axios.post(
                        `${BASIC_URL}/api/industrie-objects?status=draft&locale=${lang}`,
                        payload,
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    createdDocumentId = res.data.data.documentId;
                }
            }

            setSuccess(t('company_form.success'));
            onUpdate();
        } catch (err) {
            console.error('Save failed:', err);
            setError(t('company_form.error'));
        } finally {
            setSaving(false);
        }
    };

    const activeData = formData[activeLang];
    const sharedData = formData.shared;

    return (
        <Box sx={{ mb: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Tabs value={activeLang} onChange={(e, newLang) => setActiveLang(newLang)}>
                    <Tab value="de" label={t('languages.de')} />
                    <Tab value="en" label={t('languages.en')} />
                </Tabs>
                <IconButton onClick={handleDelete}>
                    <DeleteIcon />
                </IconButton>
            </Box>

            <TextField fullWidth margin="normal" label={t('company_form.company')} value={userCompany} disabled sx={inputStyle} />

            <TranslatableField
                label={t('company_form.branch')}
                lang={activeLang}
                value={activeData.industrieObject || ''}
                otherLangValue={formData[getOppositeLang(activeLang)].industrieObject}
                onChange={(val) => handleChange(activeLang, 'industrieObject', val)}
                sx={inputStyle}
            />

            <TextField
                fullWidth
                required
                error={!!fieldErrors['shared.email']}
                margin="normal"
                label={t('company_form.email')}
                type="email"
                value={sharedData.email || ''}
                onChange={(e) => handleChange('shared', 'email', e.target.value)}
                sx={inputStyle}
            />
            <TextField
                fullWidth
                margin="normal"
                label={t('company_form.website')}
                type="url"
                value={sharedData.website || ''}
                onChange={(e) => handleChange('shared', 'website', e.target.value)}
                sx={inputStyle}
            />
            <TextField
                fullWidth
                margin="normal"
                label={t('company_form.website_career')}
                type="url"
                value={sharedData.websiteCareer || ''}
                onChange={(e) => handleChange('shared', 'websiteCareer', e.target.value)}
                sx={inputStyle}
            />

            <TranslatableField
                label={t('company_form.contact')}
                lang={activeLang}
                value={activeData.contact || ''}
                otherLangValue={formData[getOppositeLang(activeLang)].contact}
                onChange={(val) => handleChange(activeLang, 'contact', val)}
                sx={inputStyle}
                multiline
                rows={3}
            />

            <TextField
                fullWidth
                required
                error={!!fieldErrors['shared.street']}
                margin="normal"
                label={t('company_form.street')}
                value={sharedData.street || ''}
                onChange={(e) => handleAddressChange('street', e.target.value)}
                sx={inputStyle}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                    fullWidth
                    required
                    error={!!fieldErrors['shared.zip']}
                    margin="normal"
                    label={t('company_form.zip')}
                    type="number"
                    value={sharedData.zip || ''}
                    onChange={(e) => handleAddressChange('zip', e.target.value)}
                    sx={inputStyle}
                />
                <TextField
                    fullWidth
                    required
                    error={!!fieldErrors['shared.location']}
                    margin="normal"
                    label={t('company_form.location')}
                    value={sharedData.location || ''}
                    onChange={(e) => handleAddressChange('location', e.target.value)}
                    sx={inputStyle}
                />
            </Box>

            <CoordinatePickerMap
                latitude={sharedData.latitude}
                longitude={sharedData.longitude}
                showSaarlandBorderAlert={showSaarlandBorderAlert}
            />

          <Box sx={{ mb: 4 }}>
            <TranslatableAutocomplete
              label={t('company_form.technologies')}
              lang={activeLang}
              value={activeData.technologies || []}
              otherLangValue={formData[getOppositeLang(activeLang)].technologies}
              onChange={(val) => handleChange(activeLang, 'technologies', val)}
              options={availableTechnologies}
              tagName={'technologies'}
              sx={inputStyle}
            />
            <TranslatableAutocomplete
              label={t('company_form.type_of_industries')}
              lang={activeLang}
              value={activeData.typeOfIndustries || []}
              otherLangValue={formData[getOppositeLang(activeLang)].typeOfIndustries}
              onChange={(val) => handleChange(activeLang, 'typeOfIndustries', val)}
              options={availableTypeOfIndustries}
              tagName={'type-of-industries'}
              sx={inputStyle}
            />
            <TranslatableAutocomplete
              label={t('company_form.product_services')}
              lang={activeLang}
              value={activeData.productsAndServices || []}
              otherLangValue={formData[getOppositeLang(activeLang)].productsAndServices}
              onChange={(val) => handleChange(activeLang, 'productsAndServices', val)}
              options={availableProductServices}
              tagName={'products-services'}
              sx={inputStyle}
            />
            <TranslatableAutocomplete
              label={t('company_form.field_profiles')}
              lang={activeLang}
              value={activeData.fieldProfiles || []}
              otherLangValue={formData[getOppositeLang(activeLang)].fieldProfiles}
              onChange={(val) => handleChange(activeLang, 'fieldProfiles', val)}
              options={availableFieldProfiles}
              tagName={'field-profiles'}
              sx={inputStyle}
            />
          </Box>

            {error && <Box mt={2}><Alert severity="error">{error}</Alert></Box>}
            {success && <Box mt={2}><Alert severity="success">{success}</Alert></Box>}

            <Box mt={2} textAlign="center">
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    sx={{
                        backgroundColor: '#0063a6',
                        fontWeight: 600,
                        px: 4,
                        '&:hover': {
                            backgroundColor: '#94c11c',
                        },
                    }}
                >
                    {saving ? t('company_form.saving') : t('company_form.save')}
                </Button>
            </Box>
        </Box>
    );
};

export default CompanyEntry;
